var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useCallback, useEffect } from 'react';
import './ImageDropzone.scss';
import { backendUrl } from '@common/contracts';
import { uploadGenericImage } from '../../api/images';
// Reads an image file's natural pixel dimensions without uploading it.
function readImageSize(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.naturalWidth, height: img.naturalHeight }); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read the image.')); };
        img.src = url;
    });
}
// Redraw a file at an exact size. Used for the offered map resize — the canvas does
// a plain bilinear scale, which is why the offer says quality will suffer.
function resizeImageFile(file, width, height) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Could not prepare the canvas.'));
                return;
            }
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Could not resize the image.'));
                    return;
                }
                const name = file.name.replace(/\.[^.]+$/, '') + `-${width}x${height}.webp`;
                resolve(new File([blob], name, { type: 'image/webp' }));
            }, 'image/webp', 0.92);
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read the image.')); };
        img.src = url;
    });
}
function ImageDropzone({ label, currentImageUrl, onUploadSuccess, requireSquare, recommendedSize, disabled = false, 'aria-labelledby': ariaLabelledby, kind }) {
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    // An off-size image waiting for the author's call: resize it, or upload as is.
    const [offSize, setOffSize] = useState(null);
    const normalizeUrl = (url) => {
        if (!url)
            return null;
        return url.startsWith('http') ? url : `${backendUrl}${url}`;
    };
    const [previewFullUrl, setPreviewFullUrl] = useState(normalizeUrl(currentImageUrl));
    useEffect(() => {
        setPreviewFullUrl(normalizeUrl(currentImageUrl));
    }, [currentImageUrl]);
    const handleUpload = useCallback((file_1, ...args_1) => __awaiter(this, [file_1, ...args_1], void 0, function* (file, skipSizeCheck = false) {
        if (!file || !file.type.startsWith('image/')) {
            setError('Please drop an image file (png, jpg, gif, etc.).');
            return;
        }
        // Level backgrounds must be square — reject non-square images before uploading.
        if ((requireSquare || recommendedSize) && !skipSizeCheck) {
            let size;
            try {
                size = yield readImageSize(file);
            }
            catch (_a) {
                setError('Could not read the image dimensions. Please try another file.');
                return;
            }
            if (requireSquare && size.width !== size.height) {
                setError(`The background must be square (equal width and height). This image is ${size.width}×${size.height}.`);
                return;
            }
            // Off the size the game needs: don't reject — offer the resize (see the panel
            // below), because a wrong-size map lands the cells off the picture.
            if (recommendedSize && (size.width !== recommendedSize.width || size.height !== recommendedSize.height)) {
                setError(null);
                setOffSize({ file, width: size.width, height: size.height });
                return;
            }
        }
        setOffSize(null);
        setIsUploading(true);
        setError(null);
        try {
            const result = yield uploadGenericImage(file, kind);
            if (!result.success || !result.imageUrl) {
                throw new Error(result.error || 'Upload failed');
            }
            const newRelativePath = result.imageUrl; // Relative path like /uploads/<id>
            onUploadSuccess(newRelativePath); // Pass relative path to parent
            // Set FULL URL for preview
            setPreviewFullUrl(normalizeUrl(newRelativePath));
        }
        catch (err) {
            console.error('Upload error:', err);
            setError(`Upload failed: ${err.message}`);
            // Revert preview to original full URL on error
            setPreviewFullUrl(normalizeUrl(currentImageUrl));
        }
        finally {
            setIsUploading(false);
        }
    }), [onUploadSuccess, currentImageUrl, requireSquare, recommendedSize]);
    // Author accepted the offer: redraw at the canonical size, then upload that.
    const handleResizeAndUpload = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!offSize || !recommendedSize)
            return;
        setIsUploading(true);
        setError(null);
        try {
            const resized = yield resizeImageFile(offSize.file, recommendedSize.width, recommendedSize.height);
            setOffSize(null);
            yield handleUpload(resized, true);
        }
        catch (err) {
            setError((err === null || err === void 0 ? void 0 : err.message) || 'Could not resize the image.');
        }
        finally {
            setIsUploading(false);
        }
    }), [offSize, recommendedSize, handleUpload]);
    // --- Drag and Drop Handlers --- 
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setTimeout(() => setIsDragging(false), 50);
    }, []);
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
        setIsDragging(true);
    }, []);
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        setError(null);
        if (disabled)
            return;
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleUpload(e.dataTransfer.files[0]);
            e.dataTransfer.clearData();
        }
    }, [handleUpload, disabled]);
    // --- Click to Upload Handlers --- 
    const fileInputRef = React.useRef(null);
    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleUpload(e.target.files[0]);
        }
    };
    const triggerFileInput = () => {
        var _a;
        if (disabled)
            return;
        (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); // Use ref to click hidden input
    };
    return (_jsxs("div", { className: 'form-group image-dropzone-container', children: [label && _jsx("label", { children: label }), _jsxs("div", { className: `image-dropzone ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''} ${disabled ? 'disabled' : ''}`, onDragEnter: disabled ? undefined : handleDragEnter, onDragLeave: disabled ? undefined : handleDragLeave, onDragOver: disabled ? undefined : handleDragOver, onDrop: handleDrop, onClick: triggerFileInput, "aria-labelledby": ariaLabelledby, children: [_jsx("input", { type: "file", ref: fileInputRef, accept: "image/*", onChange: handleFileSelect, style: { display: 'none' } }), previewFullUrl && !isUploading && (_jsx("img", { src: previewFullUrl, alt: "Background preview", className: "image-preview" })), !previewFullUrl && !isUploading && (_jsx("div", { className: "drop-prompt", children: "Drop image here, or click to select" })), isUploading && (_jsx("div", { className: "upload-indicator", children: "Uploading..." }))] }), error && _jsx("div", { className: "error-message-small", children: error }), offSize && recommendedSize && (_jsxs("div", { className: "dropzone-size-offer", children: [_jsxs("p", { className: "dz-offer-head", children: ["This map is ", offSize.width, "\u00D7", offSize.height, " \u2014 the game needs", ' ', _jsxs("b", { children: [recommendedSize.width, "\u00D7", recommendedSize.height] }), "."] }), _jsxs("div", { className: "dz-offer-actions", children: [_jsx("button", { type: "button", className: "dz-offer-resize", disabled: isUploading, onClick: handleResizeAndUpload, children: "Resize and upload" }), _jsx("button", { type: "button", className: "dz-offer-cancel", disabled: isUploading, onClick: () => setOffSize(null), children: "Cancel" })] }), _jsx("p", { className: "dz-offer-note", children: "Resizing softens the picture a little." })] }))] }));
}
export default ImageDropzone;
